/**
 * Shared labels and table/read-only formatting for HERA + county remittance
 * fields on license-type configuration and ordinance versions.
 *
 * ## Visibility (must match `JurisdictionForm` HERA Remittance section)
 *
 * - **Registration + county** (`hera_reg_*`, `county_reg_*`): always shown.
 * - **HERA renewal** (`hera_renewal_*`): only when renewal uses a separate fee
 *   (`renewal_fee_same_as_registration` is false).
 * - **HERA late** (`hera_late_*`): only when first or recurring late fee is enabled.
 * - **HERA de-registration** (`hera_deregistration_*`): only when
 *   `deregistration_fee_type` is not `none` (case-insensitive; supports API `NONE`).
 * - **HERA inspection** (`hera_inspection_*`): only when `inspection_fee_structure`
 *   is set and not `none`.
 * - **`hera_retain_remittance`**: always shown (same as jurisdiction form).
 *
 * Ordinance read-only and ordinance draft editors use
 * `getVisibleHeraRemittanceScalarKeys` so they stay aligned with the jurisdiction panel.
 *
 * ## Audit: jurisdiction vs ordinance UI (intentional differences)
 *
 * **Jurisdiction (`JurisdictionForm`)** — full editor with TanStack Form:
 * - Amount fields use `<Input type="number" min="0" step="0.01">`; percentage
 *   fields use `type="number"` with 0–100 bounds and mutual exclusivity
 *   validation (amount XOR percentage) per fee family (registration, late,
 *   inspection, de-registration).
 * - HERA late labels: **“HERA Late Fee Amount”** / **“HERA Late Fee Percentage”**.
 * - `hera_retain_remittance` is a switch; helper text matches
 *   `formatHeraRetainRemittanceForTable` when fees are visible.
 *
 * **Ordinance workspace (`OrdinanceRuleFormFields`)** — draft based on
 * `OrdinanceVersionCreateRequestSchema`:
 * - Same **field labels** as the jurisdiction form (sourced from
 *   `HERA_FEE_FIELD_LABELS`); inputs remain **text** (`readMoney`-style) so API
 *   string | null shapes round-trip without coercing in the ordinance draft
 *   pipeline.
 * - No live cross-field validation in the ordinance panel (save validates on
 *   server); jurisdiction form enforces XOR client-side.
 *
 * **Dashboard tables** — both use `formatHeraScalarFeeForTable` /
 * `formatHeraRetainRemittanceForTable` with the “Show fees” toggle so masked
 * cells show `***` consistently. Ordinance rows encode retain as `"Yes"` /
 * `"No"` strings from `rowFromSource`; jurisdiction rows use booleans on the
 * nested config — both normalize through `parseHeraRetainRemittanceInput`.
 *
 * **Ordinance read-only detail (`OrdinanceDetailPane`)** — optional
 * `showFeeAmounts`; when false, HERA read-only lines use the same `***` mask as
 * tables. Jurisdiction sheet does not use this pane (it uses `JurisdictionForm`).
 */

import type { OrdinanceVersionCreateRequestSchema } from "@/hera-api/client/types.gen";

export const MASKED_FEE_PLACEHOLDER = "***" as const;

/** Label for the retain switch / read-only row (matches `JurisdictionForm`). */
export const HERA_RETAIN_REMITTANCE_FIELD_LABEL =
  "HERA Retains Remittance" as const;

/** Labels aligned with `JurisdictionForm` HERA Remittance section. */
export const HERA_FEE_FIELD_LABELS = {
  hera_reg_amount: "HERA Registration Amount",
  hera_reg_percentage: "HERA Registration Percentage",
  hera_renewal_amount: "HERA Renewal Amount",
  hera_renewal_percentage: "HERA Renewal Percentage",
  county_reg_amount: "County Registration Amount",
  county_reg_percentage: "County Registration Percentage",
  hera_late_amount: "HERA Late Fee Amount",
  hera_late_percentage: "HERA Late Fee Percentage",
  hera_inspection_amount: "HERA Inspection Amount",
  hera_inspection_percentage: "HERA Inspection Percentage",
  hera_deregistration_amount: "HERA De-registration Amount",
  hera_deregistration_percentage: "HERA De-registration Percentage",
} as const;

export type HeraFeeFieldLabelKey = keyof typeof HERA_FEE_FIELD_LABELS;

/**
 * Normalize booleans from API models, ordinance drafts, or flattened table rows
 * (`"Yes"` / `"No"` from `rowFromSource`).
 */
export function coerceLtcBooleanLike(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (t === "yes" || t === "true" || t === "1") return true;
    if (t === "no" || t === "false" || t === "0" || t === "") return false;
  }
  return Boolean(value);
}

export type HeraRemittanceVisibilityContext = {
  first_late_fee_enabled?: unknown;
  recurring_late_fee_enabled?: unknown;
  inspection_fee_structure?: string | null | undefined;
  deregistration_fee_type?: string | null | undefined;
  renewal_fee_same_as_registration?: unknown;
};

export type CountyRemittanceFields = {
  county_reg_amount?: string | number | null;
  county_reg_percentage?: number | null | undefined;
};

/** True when county registration amount or percentage is set on the license type config. */
export function hasCountyRemittanceConfigured(
  config: CountyRemittanceFields,
): boolean {
  const amount = config.county_reg_amount;
  const amountPresent =
    amount !== null && amount !== undefined && String(amount).trim() !== "";
  const percentage = config.county_reg_percentage;
  const percentagePresent = percentage !== null && percentage !== undefined;
  return amountPresent || percentagePresent;
}

export function validateJurisdictionCountyForRemittance(
  county: string | null | undefined,
  config: CountyRemittanceFields,
): string | undefined {
  if (!hasCountyRemittanceConfigured(config)) {
    return undefined;
  }
  if (!county?.trim()) {
    return "County is required when county remittance is configured";
  }
  return undefined;
}

export function lateFeesEnabledForHeraRemittance(
  ctx: Pick<
    HeraRemittanceVisibilityContext,
    "first_late_fee_enabled" | "recurring_late_fee_enabled"
  >,
): boolean {
  return (
    coerceLtcBooleanLike(ctx.first_late_fee_enabled) ||
    coerceLtcBooleanLike(ctx.recurring_late_fee_enabled)
  );
}

function inspectionFeeStructureActive(
  structure: string | null | undefined,
): boolean {
  if (structure == null || structure === "") return false;
  return structure.toLowerCase() !== "none";
}

function deregistrationFeeActive(dereg: string | null | undefined): boolean {
  if (dereg == null || dereg === "") return false;
  return dereg.toLowerCase() !== "none";
}

/** True when the ordinance stores a renewal fee that is not the registration fee. */
function separateRenewalFee(value: unknown): boolean {
  if (value === false || value === 0) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "false" || normalized === "no" || normalized === "0";
  }
  return false;
}

/** Display order matches `JurisdictionForm` HERA Remittance layout. */
const HERA_REMITTANCE_SCALAR_DISPLAY_ORDER = [
  "hera_reg_amount",
  "hera_reg_percentage",
  "county_reg_amount",
  "county_reg_percentage",
  "hera_renewal_amount",
  "hera_renewal_percentage",
  "hera_late_amount",
  "hera_late_percentage",
  "hera_deregistration_amount",
  "hera_deregistration_percentage",
  "hera_inspection_amount",
  "hera_inspection_percentage",
] as const satisfies readonly HeraFeeFieldLabelKey[];

export function getVisibleHeraRemittanceScalarKeys(
  ctx: HeraRemittanceVisibilityContext,
): HeraFeeFieldLabelKey[] {
  const late = lateFeesEnabledForHeraRemittance(ctx);
  const insp = inspectionFeeStructureActive(ctx.inspection_fee_structure);
  const dereg = deregistrationFeeActive(ctx.deregistration_fee_type);
  const renewal = separateRenewalFee(ctx.renewal_fee_same_as_registration);

  return HERA_REMITTANCE_SCALAR_DISPLAY_ORDER.filter((key) => {
    if (key === "hera_renewal_amount" || key === "hera_renewal_percentage") {
      return renewal;
    }
    if (key === "hera_late_amount" || key === "hera_late_percentage") {
      return late;
    }
    if (
      key === "hera_deregistration_amount" ||
      key === "hera_deregistration_percentage"
    ) {
      return dereg;
    }
    if (
      key === "hera_inspection_amount" ||
      key === "hera_inspection_percentage"
    ) {
      return insp;
    }
    return true;
  });
}

export function patchClearHeraLateRemittanceFields(): Pick<
  OrdinanceVersionCreateRequestSchema,
  "hera_late_amount" | "hera_late_percentage"
> {
  return { hera_late_amount: null, hera_late_percentage: null };
}

export function patchClearHeraInspectionRemittanceFields(): Pick<
  OrdinanceVersionCreateRequestSchema,
  "hera_inspection_amount" | "hera_inspection_percentage"
> {
  return { hera_inspection_amount: null, hera_inspection_percentage: null };
}

export function patchClearHeraDeregistrationRemittanceFields(): Pick<
  OrdinanceVersionCreateRequestSchema,
  "hera_deregistration_amount" | "hera_deregistration_percentage"
> {
  return {
    hera_deregistration_amount: null,
    hera_deregistration_percentage: null,
  };
}

export function formatHeraScalarFeeForTable(
  showFeeAmounts: boolean,
  value: string | number | null | undefined,
): string {
  if (!showFeeAmounts) return MASKED_FEE_PLACEHOLDER;
  if (value === null || value === undefined) return "";
  const s = String(value).trim();
  return s === "" ? "" : String(value);
}

/**
 * Normalize API / row values to a boolean (retains remittance when true).
 * Ordinance list rows use `"Yes"` / `"No"` from `rowFromSource`.
 */
export function parseHeraRetainRemittanceInput(value: unknown): boolean {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  if (value === "Yes") return true;
  if (value === "No") return false;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (t === "true" || t === "1") return true;
    if (t === "false" || t === "0" || t === "") return false;
  }
  return Boolean(value);
}

export function formatHeraRetainRemittanceForTable(
  showFeeAmounts: boolean,
  value: unknown,
): string {
  if (!showFeeAmounts) return MASKED_FEE_PLACEHOLDER;
  return parseHeraRetainRemittanceInput(value)
    ? "Retains Remittance"
    : "Invoices for Payment";
}

/** Read-only / detail pane: same masking as tables. */
export function formatHeraScalarForDetailPane(
  showFeeAmounts: boolean,
  value: string | number | null | undefined,
): string {
  if (!showFeeAmounts) return MASKED_FEE_PLACEHOLDER;
  if (value === null || value === undefined) return "—";
  const s = String(value).trim();
  return s === "" ? "—" : String(value);
}

export function formatHeraRetainRemittanceForDetailPane(
  showFeeAmounts: boolean,
  value: unknown,
): string {
  if (!showFeeAmounts) return MASKED_FEE_PLACEHOLDER;
  return parseHeraRetainRemittanceInput(value)
    ? "Retains Remittance"
    : "Invoices for Payment";
}
